import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { getComicById, getRelatedComics, categories } from '@/mocks/comics';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || '');

  if (!comic) {
    return <NotFound />;
  }

  const related = getRelatedComics(comic.id, 4);
  const categoryLabel = categories.find((c) => c.slug === comic.category)?.name || comic.category;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫在线免费阅读`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.chapters[0]?.createdAt,
    dateModified: comic.updatedAt,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://hanmandq.com/article/${comic.id}`,
    },
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: `${categoryLabel}漫画`, href: `/category/${comic.category}` },
    { label: comic.title },
  ];

  return (
    <>
      <Helmet>
        <title>{`${comic.title} - 韩漫在线免费阅读_韩漫大全热门合集`}</title>
        <meta name="description" content={`${comic.title}，${comic.author}创作的${categoryLabel}类好看韩国漫画。${comic.description}韩漫漫画网站支持韩漫在线免费阅读，尽在韩漫大全。`} />
        <meta name="keywords" content={`${comic.title},${categoryLabel}漫画,韩漫漫画网站,好看韩国漫画,韩漫在线免费阅读,韩漫大全,热门合集`} />
        <link rel="canonical" href={`https://hanmandq.com/article/${comic.id}`} />
        <meta property="og:title" content={`${comic.title} - 韩漫在线免费阅读`} />
        <meta property="og:description" content={comic.description} />
        <meta property="og:url" content={`https://hanmandq.com/article/${comic.id}`} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={comic.coverImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${comic.title} - 韩漫在线免费阅读`} />
        <meta name="twitter:description" content={comic.description} />
        <meta name="twitter:image" content={comic.coverImage} />
      </Helmet>

      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb items={breadcrumbItems} />

          <article className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-[300px] flex-shrink-0">
                <div className="aspect-[5/7] rounded-lg overflow-hidden sticky top-24">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - 好看韩国漫画，韩漫在线免费阅读`}
                    title={`${comic.title} - 韩漫在线免费阅读，韩漫大全`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 mb-5">
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-user-line text-sm text-foreground-500"></i>
                    </div>
                    <span className="text-sm text-foreground-600">{comic.author}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm text-accent-500"></i>
                    </div>
                    <span className="text-sm text-foreground-600 font-semibold">{comic.rating}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-eye-line text-sm text-foreground-500"></i>
                    </div>
                    <span className="text-sm text-foreground-600">{comic.views.toLocaleString()} 次阅读</span>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap ${
                    comic.status === '连载中'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-background-200 text-foreground-700'
                  }`}>
                    {comic.status}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-5">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100/70 rounded-lg p-4 mb-6">
                  <h2 className="text-sm font-semibold text-foreground-950 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div>
                  <h2 className="text-lg font-semibold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                    <a href="#chapter-list">章节列表</a>
                  </h2>
                  <div className="space-y-2">
                    {comic.chapters.map((chapter) => (
                      <div
                        key={chapter.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-background-100/70 hover:bg-primary-50 transition-colors cursor-pointer group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                            <span className="text-xs font-semibold">{chapter.number}</span>
                          </div>
                          <span className="text-sm text-foreground-800 group-hover:text-primary-600 transition-colors">
                            {chapter.title}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-foreground-400">{chapter.createdAt}</span>
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-arrow-right-s-line text-foreground-400 group-hover:text-primary-500 transition-colors"></i>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {related.length > 0 && (
              <section className="mt-12 pt-10 border-t border-background-200">
                <h2 className="text-xl font-bold text-foreground-950 mb-5" style={{ fontFamily: 'var(--font-heading)' }}>
                  <a href="#related-comics">相关推荐</a>
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                  {related.map((r) => (
                    <ComicCard key={r.id} comic={r} size="small" />
                  ))}
                </div>
              </section>
            )}
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}